import {
  ArrowTrendingUpIcon,
  Bars3Icon,
  BanknotesIcon,
  CreditCardIcon,
  HomeIcon,
  TruckIcon,
  QueueListIcon,
  ArrowDownTrayIcon,
  Square3Stack3DIcon,
  WalletIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import type { LinkProps } from "@tanstack/react-router";
import { useEffect, useRef, type ComponentType, type ReactNode } from "react";

import type { AnimatedIconName } from "./animated-icons.js";
import { NavLink } from "./nav-link.js";
import { useScrollLock } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";

type NavItem = {
  to: NonNullable<LinkProps["to"]>;
  labelKey: string;
  /** The static icon: shown until the animation's chunk arrives, and for good under reduced motion. */
  icon: ComponentType<{ className?: string }>;
  /** The Lottie file that plays on hover, focus or click. */
  animation: AnimatedIconName;
};

/**
 * The sections. Adding a feature adds one line here and nothing else — which is the only reason this
 * is a list rather than eight hand-written elements.
 *
 * ==========================================================================================
 * THE ORDER IS WHAT THE HOUSEHOLD OWNS, THEN WHAT ITS MONTH DOES, AND NOT THE BUILD ORDER
 * ==========================================================================================
 *
 * It WAS the order the roadmap builds them in — 006, 008, 009, 015, 016, 018 — with Income and Cards
 * moved by hand. A build log is not a reading order, and it left one real incoherence: Investments is
 * a net worth pillar exactly as Savings and Properties are, and it sat SEVENTH, separated from them by
 * the whole cash-flow group. The three things a household owns were not adjacent.
 *
 * Two places already state a canonical order and they agree with each other:
 *
 *   `net-worth-chart.tsx`   the stack's layers — Savings, Investments, Properties, Mortgages
 *   `reporting/freshness.ts` the pillar list — savings, investments, properties, expenses, income
 *
 * Both put the BALANCE before the FLOW and order the assets most-liquid-first, which is also how the
 * Dashboard reads down the page: net worth and its chart, then In/Out/Left and the flow charts. This
 * list is now the same sentence a third time.
 *
 * ==========================================================================================
 * WHICH ANIMATION DRAWS WHICH SECTION IS DECIDED HERE
 * ==========================================================================================
 *
 * The files are named for what they DRAW — `car`, `coin`, `home` — because that is what they are, and
 * the mapping to sections is a product judgement that belongs beside the sections rather than in the
 * filenames. Seven of the nine are obvious. Two are calls worth flagging, and both are a one-line
 * swap here if they are the wrong ones:
 *
 *   DASHBOARD uses `car`, which draws a SPEEDOMETER rather than a car — "system-regular-33-speed". A
 *   dashboard is a set of gauges, so it fits better than the file's name suggests.
 *
 *   ACTIVITY uses `coin`, which is the weakest of the nine: the activity log is about events, not
 *   money, and a coin says money. It is what is left, and every event in the log is about money
 *   changing, which is the argument for it.
 */
/*
 * THIS CONSTANT HOLDS KEYS, NOT SENTENCES — feature 021.
 *
 * A module-scope `t("…")` is evaluated once, when the module is first imported, so the label is
 * frozen in whichever language was loaded at that moment. It renders correctly for whoever opens the
 * application and wrongly for whoever switches, and nothing about it looks wrong.
 */
const SECTIONS: NavItem[] = [
  { to: "/", labelKey: "nav.dashboard", icon: Square3Stack3DIcon, animation: "dashboard" },

  // ---- what the household owns, most liquid first -------------------------
  { to: "/savings", labelKey: "nav.savings", icon: WalletIcon, animation: "savings" },
  {
    to: "/investments",
    labelKey: "nav.investments",
    icon: ArrowTrendingUpIcon,
    animation: "investments",
  },
  { to: "/properties", labelKey: "nav.properties", icon: HomeIcon, animation: "home" },
  /*
   * LAST AMONG THE ASSETS, and the ordering rule above is why it is not obvious.
   *
   * "Most liquid first" would arguably put a car ABOVE a house — one sells in a week, the other in
   * months. It sits here instead because it is the smallest pillar and the only one whose defining
   * behaviour is to shrink, which is the order the net worth chart stacks its layers in and the
   * order the Dashboard reads down the page.
   */
  { to: "/vehicles", labelKey: "veh.nav", icon: TruckIcon, animation: "car" },

  // ---- what its month does ------------------------------------------------
  // Income sits BEFORE Expenses, which is the order a household reads them in and the order the
  // Dashboard's own cards state them: In, then Out. The two are never composed on either page —
  // that is the Dashboard's job. (`freshness.ts` lists expenses first; that is a SQL column order
  // and says nothing about reading.)
  { to: "/income", labelKey: "nav.income", icon: ArrowDownTrayIcon, animation: "coin" },
  {
    to: "/expenses",
    labelKey: "nav.expenses",
    icon: BanknotesIcon,
    animation: "recurring-expenses",
  },
  // Beside Expenses, because that is what a card is for — and its own entry, because setting one up
  // is a once-a-year task that has no business lengthening the page opened every month.
  { to: "/cards", labelKey: "nav.cards", icon: CreditCardIcon, animation: "wallet" },

  // ---- and the log across all of it, which is history rather than a place --
  { to: "/activity", labelKey: "nav.activity", icon: QueueListIcon, animation: "activity" },
];

/**
 * The navigation rail.
 *
 * Two groups pushed apart by a spacer: where you are going, and what you do to the session. That
 * separation is the whole information design of the thing — Settings and Sign out are not
 * destinations of the same kind as Savings, and putting them at the far end says so.
 *
 * ==========================================================================================
 * IT IS 224px OF ICONS AND WORDS, WHERE IT WAS 80px OF ICONS ALONE
 * ==========================================================================================
 *
 * `md:w-56` HERE AND `md:pl-56` IN `AppShell` ARE ONE NUMBER WRITTEN TWICE, and the note in that
 * file explains why nothing can catch them drifting: the rail is `fixed`, so the padding is the only
 * thing holding the content clear of it, and a rail that overlapped would read as a margin set too
 * small rather than as a bug.
 *
 * The icons are 24px now rather than 34px. An icon that is the entire control has to be large enough
 * to aim at; an icon with its name beside it is the mark in front of a row, and one taller than the
 * word it labels reads as an illustration. `nav-pill.ts` holds both halves of that change.
 *
 * The 64px header above the sections is not decoration — it is what lets the first nav row and the
 * household name in `AppShell`'s bar begin on the same line, which is the only vertical relationship
 * between the rail and the content. It replaces the shared `md:pt-12` that did the same job before
 * there was a bar to align to, and the two numbers still have to move together.
 *
 * ==========================================================================================
 * WHY IT IS FIXED TO THE VIEWPORT AND NOT TO THE CONTENT COLUMN
 * ==========================================================================================
 *
 * It used to be a `sticky` flex item INSIDE the shell's centred `max-w-[1400px]` container, which
 * meant its horizontal position was a function of the window width: at 1400px it sat at the left
 * edge, and at 2560px it floated 580px in from it with nothing to its left. A navigation that moves
 * when you resize is not a landmark, and a landmark is the entire job of primary navigation.
 *
 * So it is `fixed` at BOTH breakpoints now — a bar along the bottom edge on a phone, a full-height
 * column against the left edge above `md` — and `AppShell` reserves the column's width with
 * padding rather than with a flex sibling. The content inside keeps centring; the rail no longer
 * participates in that.
 *
 * It also starts at the top rather than 10rem down. The old offset lined the first icon up with the
 * page heading, which was coherent while the rail was part of the content column and is not now:
 * a column pinned to the viewport reads as chrome, and chrome begins where the viewport does.
 *
 * ==========================================================================================
 * IT IS DESKTOP-ONLY NOW. THE PHONE GETS `MobileNav` INSTEAD
 * ==========================================================================================
 *
 * Below `md` this used to become a bar of icons along the bottom edge, and that bar was the cause of
 * both things reported on 2026-08-21. MEASURED, not reasoned about — `mobile-nav.spec.ts` printed
 * these figures against the old build:
 *
 *   Eight 48px pills with 4px gaps inside 8px of padding is a 412px line. A phone viewport is 390.
 *   `justify-center` overflows a too-wide flex line EQUALLY ON BOTH SIDES, so the list sat at
 *   `left: -11px` and ran to `right: 401px`, with the first and last icons clipped off both edges
 *   and unreachable. Every page had it, because the bar was on every page.
 *
 * ONE HONEST CAVEAT ABOUT THE SYMPTOM. In Chromium's device emulation the document's own
 * `scrollWidth` stayed at 390 — a `position: fixed` element does not extend the scrollable area
 * there — so the "you can scroll sideways" half does not reproduce in the test harness. It is a
 * mobile-browser behaviour. The 22px of navigation hanging off the two edges is the cause either
 * way, and it is the half that can be asserted; the first version of that assertion measured
 * `scrollWidth` and passed green over this exact bug.
 *
 * A drawer has no such limit: it is a column, so the list can be as long as the roadmap gets, and it
 * can carry the labels the bar could never fit. `AppShell` no longer pads the content to clear
 * anything, because there is nothing fixed along the bottom any more.
 */
export function SideNav(): ReactNode {
  const t = useT();
  return (
    <nav
      aria-label={t("nav.primary")}
      className={
        // No fill, no border, no blur — the rail sits directly on the gradient and shares that ground
        // with the bar above the content, which is what makes the two read as one piece of chrome.
        //
        // `hidden md:flex`: below `md` there is no rail at all, only `MobileNav`'s drawer. Two
        // navigations both labelled "Primary" would be an ambiguous landmark if they were ever
        // visible together, and they never are — this is `display:none` on a phone and the drawer is
        // a closed `<dialog>`, which is `display:none` on a desktop.
        "fixed inset-y-0 left-0 z-20 hidden w-56 flex-col items-stretch justify-start " +
        "px-3 md:flex"
      }
    >
      {/*
       * The wordmark, and the 64px line the content's bar aligns to.
       *
       * Always shown, because this whole element is now desktop-only. It used to be `hidden md:flex`,
       * for a phone bar along the bottom edge where a product name would have sat in the middle of the
       * icons saying nothing about where anything goes. `MobileNav` draws its own.
       */}
      <span className="flex h-16 shrink-0 items-center px-3 text-base font-semibold tracking-wide text-ink">
        GLADIS
      </span>

      {/*
       * `md:min-h-0` and `md:overflow-y-auto` on the SECTION LIST, not on the nav.
       *
       * The rail is `fixed inset-y-0` on desktop: a fixed height with a `flex-1` spacer above a
       * pinned Settings and Sign out. A flex child's default `min-height: auto` refuses to shrink
       * below its content, so once the section list is taller than the viewport the spacer collapses
       * to zero and the bottom group is pushed OFF-SCREEN — reachable by nothing, and invisible to a
       * member who never scrolls a nav bar because nav bars do not scroll.
       *
       * Feature 015 was the eighth entry and the one that crossed the line; 010's Investments and
       * 013's Dashboard are still to come, so the list scrolls rather than the footer falling away.
       * `scrollbar-none` because a permanent gutter in a 64px rail is most of the rail.
       */}
      <ul className="flex min-h-0 flex-col items-stretch gap-1 overflow-y-auto [scrollbar-width:none]">
        {SECTIONS.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              label={t(item.labelKey)}
              icon={item.icon}
              animation={item.animation}
            />
          </li>
        ))}
      </ul>

      {/*
       * NOTHING BELOW THE SECTIONS ANY MORE, and the emptiness is the design.
       *
       * This rail held three more controls until 2026-08-14: the theme picker, a Settings link, and Sign
       * out, pinned below a spacer. Its own comment argued their order — "appearance, then preferences,
       * then leaving" — and that argument was about a rail with nowhere else to put them.
       *
       * All three are chrome rather than places. The theme picker and the member's own menu moved to the
       * top bar, and Settings went into that menu with them, where it splits into the two pages it always
       * was: the household, and you. WHAT IS LEFT IS EXACTLY THE HOUSEHOLD'S FINANCIAL SECTIONS, which is
       * what a list of destinations should be — and the spacer went with them, because there is no longer
       * a second group for it to separate.
       */}
    </nav>
  );
}

/**
 * The same destinations on a phone, in a drawer opened from the top left.
 *
 * ==========================================================================================
 * WHY A DRAWER AND NOT THE BAR IT REPLACES
 * ==========================================================================================
 *
 * A bottom bar has a hard limit that a list of sections does not respect: it must fit nine controls
 * across the narrowest phone. It did not — see `SideNav` for the arithmetic and the two bugs that
 * came out of it — and each new feature makes it worse, which is the wrong direction for a list the
 * roadmap keeps adding to. A drawer is a column, so it scrolls, and it has room for the LABELS the
 * bar could never carry: nine unlabelled glyphs is a memory test.
 *
 * ==========================================================================================
 * NATIVE `<dialog>`, FOR THE REASONS `@gladis/ui`'s Dialog GIVES
 * ==========================================================================================
 *
 * The focus trap, the inert background, Escape, returning focus to the trigger, and a top layer with
 * no z-index argument. A hand-rolled sheet reimplements all of it and usually skips the focus trap,
 * which is the part a keyboard user actually depends on.
 *
 * It is NOT `@gladis/ui`'s `Dialog`, though it shares the mechanism. That component is a centred,
 * capped, titled panel built for forms — it renders an `<h2>` and a description and sizes itself to
 * a field grid. A navigation sheet is full-height, flush to the left edge, and has no title of its
 * own beyond the wordmark. Passing a nav through a form dialog would mean fighting every one of
 * those decisions with overrides, and `cn` is a plain join, so overriding is a coin toss.
 *
 * MOUNTED ALWAYS, OPENED BY EFFECT. A `<dialog>` cannot be conditionally rendered and then told to
 * open — `showModal()` needs an element that is already in the document — which is the same reason
 * `ExpenseForm` and `CardForm` are always mounted.
 */
export function MobileNav({ open, onClose }: { open: boolean; onClose: () => void }): ReactNode {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);
  /* Tells a close WE asked for from one the member asked for — see `Dialog`, same trap. */
  const closingOurselves = useRef(false);

  /*
   * THE PAGE BEHIND IT MUST NOT SCROLL, AND THE TOP LAYER DOES NOT DO THIS FOR YOU.
   *
   * A modal `<dialog>` stops the background being CLICKED. It does nothing about SCROLLING — a
   * wheel, a trackpad swipe or a touch drag anywhere over the backdrop still moves the document
   * underneath, so the sheet appears to hover over content sliding around behind it. On a phone the
   * backdrop is most of the screen and every gesture is a drag.
   *
   * `useScrollLock` rather than the four lines this held first. Those set `overflow: hidden` on the
   * root, which is the textbook fix, works in Chromium, and does NOT hold on mobile Safari — so the
   * report came back a second time after it shipped. The shared hook is also what `Dialog` uses, so
   * the drawer and every form in the application cannot answer this differently.
   */

  /*
   * THE LOCK IS DECLARED BEFORE THE `showModal` EFFECT, AND THE ORDER IS THE WHOLE FIX.
   *
   * React runs effects in declaration order. `showModal()` moves focus into the dialog and the
   * browser scrolls that focus into view, which puts the document at zero — so a lock declared after
   * it reads `window.scrollY` as 0, pins `top: 0px`, and restores the reader to the top of the page
   * on close. The symptom is indistinguishable from having no restore at all, and it was measured
   * rather than reasoned about: the probe printed `bodyTop=0px` on a page scrolled to 200.
   */

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) {
      closingOurselves.current = true;
      dialog.close();
    }
  }, [open]);

  /*
   * IT MUST CLOSE WHEN THE VIEWPORT GROWS PAST `md`, AND THIS IS NOT A TIDINESS MEASURE.
   *
   * A modal `<dialog>` makes the rest of the document inert. Hiding this one with `md:hidden` while
   * it is still OPEN would leave the whole application unclickable behind an element nobody can see —
   * a rotated phone or a resized browser window, and the app is dead with nothing on screen to
   * explain it. So the breakpoint is watched rather than styled around.
   *
   * `matchMedia` and not a resize listener: it fires on the crossing rather than on every pixel, and
   * 768px is `md` in this project's Tailwind, written here because there is no way to read a
   * breakpoint out of the framework at runtime.
   */
  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const wide = window.matchMedia("(min-width: 768px)");
    const handle = (): void => {
      if (wide.matches) onClose();
    };
    handle();
    wide.addEventListener("change", handle);
    return () => {
      wide.removeEventListener("change", handle);
    };
  }, [open, onClose]);

  return (
    <dialog
      ref={ref}
      // Named in `MobileNavTrigger`'s `aria-controls` as well — the usual cost of that attribute.
      id="primary-nav-drawer"
      aria-label={t("nav.primary")}
      onClose={() => {
        if (closingOurselves.current) {
          closingOurselves.current = false;
          return;
        }
        onClose();
      }}
      // A press on the backdrop closes it. The single-target test is enough here where `Dialog` needs
      // a pointerdown guard as well: there is no text to select in a list of links, so a drag ending
      // outside the sheet cannot be mistaken for a dismissal of half-entered work.
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      className={
        // `open:flex` and never a bare `flex`: a closed dialog is `display:none` by the UA sheet, and
        // a `display` utility on the element itself overrides that and leaves the sheet on screen
        // permanently. The same trap `Dialog` documents at length.
        "m-0 hidden h-dvh max-h-none w-72 max-w-[85vw] flex-col bg-page p-0 text-ink " +
        "backdrop:bg-black/40 open:flex md:hidden"
      }
    >
      <div className="flex h-16 shrink-0 items-center justify-between px-3">
        <span className="px-1 text-base font-semibold tracking-wide">GLADIS</span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("nav.closeMenu")}
          className="rounded-control p-2 text-muted hover:text-ink focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <XMarkIcon className="size-6" aria-hidden="true" />
        </button>
      </div>

      {/*
        NO SECOND `<nav>` LANDMARK IN HERE. The `<dialog>` itself carries `aria-label="Primary"`, and
        wrapping the list in a nav as well would announce the name twice on the way in.

        `overflow-y-auto` because the list is already nine entries and the roadmap adds to it; a phone
        in landscape is about 390px tall, which is fewer rows than that.
      */}
      <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-4">
        {SECTIONS.map((item) => (
          <li key={item.to}>
            {/*
              CLOSED ON THE WAY OUT. TanStack Router navigates without unmounting the shell, so a
              drawer left open would sit over the page it just navigated to — and being modal, it
              would make that page inert. The link is the one control here that changes what is
              underneath, so it is the one that has to close.
            */}
            <NavLink
              to={item.to}
              label={t(item.labelKey)}
              icon={item.icon}
              animation={item.animation}
              onNavigate={onClose}
            />
          </li>
        ))}
      </ul>
    </dialog>
  );
}

/**
 * The control that opens the drawer, for `AppShell`'s top bar.
 *
 * TOP LEFT, which is where it was asked for and where a phone's back-and-menu affordances live. It
 * is `md:hidden`: above that breakpoint every destination is already on screen in the rail, and a
 * button that opened a drawer duplicating a visible rail would be a second way to do a thing that
 * needs no first one.
 *
 * `aria-expanded` and `aria-controls` rather than a bare button: the pair is what tells a screen
 * reader that this discloses something and whether it is currently disclosed. The drawer's id is
 * written in both files, which is the usual cost of `aria-controls`.
 */
export function MobileNavTrigger({
  open,
  onOpen,
}: {
  open: boolean;
  onOpen: () => void;
}): ReactNode {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t("nav.openMenu")}
      aria-expanded={open}
      aria-controls="primary-nav-drawer"
      className={
        "-ml-2 rounded-control p-2 text-ink opacity-80 hover:opacity-100 " +
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none md:hidden"
      }
    >
      <Bars3Icon className="size-6" aria-hidden="true" />
    </button>
  );
}
